import { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import getUserIdFromToken from '../../Utils/authUtils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { Layout, Table, Select, Button, Card, Tag, message, Modal, Space, notification, Tabs, Tooltip } from 'antd';
import { CheckCircleOutlined, EnvironmentOutlined, VideoCameraOutlined, HomeOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import MonthlyHoursSummary from './MonthlyHoursSummary'; 
import TeacherPayout from './TeacherPayout'; 
import './Teacher.css';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';

const { Content } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

const getPlatformDisplay = (platform, t) => {
  switch (platform) {
    case 'onsite':
      return (
        <Tag color="blue" icon={<EnvironmentOutlined />}>
          {t('onsite')}
        </Tag>
      );
    case 'online':
      return (
        <Tag color="green" icon={<VideoCameraOutlined />}>
          {t('online')}
        </Tag>
      );
    case 'athome':
      return (
        <Tag color="orange" icon={<HomeOutlined />}>
          {t('athome')}
        </Tag>
      );
    default:
      return (
        <Tag color="default" icon={<QuestionCircleOutlined />}>
          -
        </Tag>
      );
  }
};

const getChangeReasonDisplay = (reason) => {
  switch (reason) {
    case "admin_adjust":
      return { text: "Admin", color: "#678d5b" };
    case "cancelled_by_student":
      return { text: "CL by S.", color: "red" };
    case "cancelled_by_teacher":
      return { text: "CL by T.", color: "orange" };
    case "class_on_hold_by_student":
      return { text: "On hold by S.", color: "blue" };
    case "class_on_hold_by_school":
      return { text: "On hold by P.", color: "green" };
    case "cancelled_same_day_teacher":
      return { text: "CSD (Teacher)", color: "#FF4500" };
    case "cancelled_same_day_student":
      return { text: "CSD (Student)", color: "#FF4500" };
    case "teacher_late":
      return { text: "Teacher Late", color: "gold" };
    case "kease_cut":
      return { text: "KEAES Cut", color: "magenta" };
    case "make_up":
      return { text: "Make Up", color: "#8A2BE2" };
    case "others":
      return { text: "Others", color: "darkgrey" };
    default:
      return { text: reason || "", color: "black" };
  }
};

const TeacherHours = () => {
  const userId = getUserIdFromToken();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1); // 1-based month
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  
  const [payoutFilter, setPayoutFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const API_URL = process.env.REACT_APP_API_URL;
  const [shownToasts, setShownToasts] = useState(new Set());

  const [isMobile, setIsMobile] = useState(window.innerWidth < 480);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatDate = (dateString) => moment(dateString).format('DD MMM YY');

  const formatScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTimeRange = (scheduleTime, scheduleHour) => {
    const startTime = moment(scheduleTime, 'HH:mm');
    const [hours, minutes] = scheduleHour.split(' ').map(value => parseInt(value));
    const endTime = startTime.clone().add(hours, 'hours').add(minutes, 'minutes');
    return `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;
  };

  const calculateTotalHours = (classes) => {
    return classes.reduce((total, cls) => {
      const [hours, minutes] = cls.schedule_hour.split(' ').map(value => parseInt(value));
      return total + (hours * 60 + minutes);
    }, 0);
  };

  const formatTotalHours = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

   const fetchClassesWithStatus = async () => {
  const token = localStorage.getItem('token');
  try {
    const [classesRes, statusRes] = await Promise.all([
      axios.get(`${API_URL}/classes/teacher/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_URL}/classes/${userId}/classes-status`, { headers: { Authorization: `Bearer ${token}` } }),
    ]);

    const classesWithStatus = classesRes.data.map(cls => {
      const statusClass = statusRes.data.find(statusCls => statusCls.classid === cls.classid);
      return {
        ...cls,
        status: statusClass ? statusClass.status : 'pending',
        payout: statusClass ? statusClass.payout : 'pending',
      };
    });

    setClasses(classesWithStatus);
    setFilteredClasses(classesWithStatus);
  } catch (error) {
    console.error(t('errorFetchingClasses'), error);
  }
};

// Call it initially
useEffect(() => {
  fetchClassesWithStatus();
}, [userId, API_URL, t]);

  useEffect(() => {
    const fetchClassChangeLogs = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClassChangeLogs(response.data);
      } catch (error) {
        console.error('Error fetching class change logs:', error);
      }
    };
    fetchClassChangeLogs();
  }, [API_URL]);


  useEffect(() => {
    classChangeLogs.forEach(log => {
      if (log.changereason === 'cancelled_by_student' && !shownToasts.has(log.id)) {
        toast.error(`Class ${log.class_code} was cancelled by the student.`, {
          position: "top-center",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setShownToasts(prev => new Set(prev).add(log.id));
      }
    });
  }, [classChangeLogs, shownToasts]);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = classes.filter(cls => {
        const classDate = new Date(cls.date);
        const classMonth = classDate.getMonth() + 1; // 1-based month
        const classYear = classDate.getFullYear();
        
        return classMonth === selectedMonth && classYear === selectedYear;
      });

      if (payoutFilter) {
        filtered = filtered.filter(cls => cls.payout === payoutFilter);
      }

      setFilteredClasses(filtered);
      setCurrentPage(1);
    };
    
    applyFilters();
  }, [selectedMonth, selectedYear, classes, payoutFilter]);

  const handleApprove = (classId, classDate, scheduleTime, scheduleHour) => {
  const now = moment().tz('Asia/Bangkok');
  
  const classDateTime = moment.tz(classDate, 'Asia/Bangkok')
    .hours(parseInt(scheduleTime.split(':')[0]))
    .minutes(parseInt(scheduleTime.split(':')[1]));
    
  const [hours, minutes] = scheduleHour.split(' ').map(value => parseInt(value));
  const endTime = classDateTime.clone().add(hours, 'hours').add(minutes, 'minutes');

  if (now.isBefore(endTime)) {
    return notification.error({
      message: t('Cannot Approve Yet'),
      description: t('Approval is only allowed after the class session has ended.'),
    });
  }

  // Check if it's been more than 3 days since class ended
  const threeDaysAfterEnd = endTime.clone().add(5, 'days');
  if (now.isAfter(threeDaysAfterEnd)) {
    return notification.warning({
      message: t('Late Approval'),
      description: t('This approval has not been made more than 5 days after the class ended.'),
    });
  }

  confirmAlert({
    title: t('Confirm Approval'),
    message: t('Are you sure you want to approve this class?'),
    buttons: [
      { label: t('Yes'), onClick: () => handleSubmitApproval(classId) },
      { label: t('No'), onClick: () => {} },
    ],
  });
};


const handleUnapproveClass = async (classId) => {
  try {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/classes/${classId}/teacher-unapprove`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });

    message.success(t('Class Unapproved Successfully'));

    // 🔁 Refresh data to show changes immediately
    await fetchClassesWithStatus();
  } catch (error) {
    console.error('❌ Error unapproving class:', error);
    message.error(t('errorUnapprovingClass'));
  }
};

// Helper function to determine button state
const getApprovalButtonState = (classDate, scheduleTime, scheduleHour) => {
  const now = moment().tz('Asia/Bangkok');
  
  const classDateTime = moment.tz(classDate, 'Asia/Bangkok')
    .hours(parseInt(scheduleTime.split(':')[0]))
    .minutes(parseInt(scheduleTime.split(':')[1]));
    
  const [hours, minutes] = scheduleHour.split(' ').map(value => parseInt(value));
  const endTime = classDateTime.clone().add(hours, 'hours').add(minutes, 'minutes');
  const threeDaysAfterEnd = endTime.clone().add(3, 'days');

  if (now.isBefore(endTime)) {
    return { 
      visible: false, 
      disabled: false, 
      isLate: false,
      tooltip: t('Approval available after class ends')
    };
  } else if (now.isAfter(threeDaysAfterEnd)) {
    return { 
      visible: true, 
      disabled: false, // Still clickable but styled differently
      isLate: true,
      tooltip: t('Late approval - more than 5 days after class ended')
    };
  } else {
    return { 
      visible: true, 
      disabled: false, 
      isLate: false,
      tooltip: null
    };
  }
};

  const handleSubmitApproval = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/classes/${classId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      notification.success({ message: t('Class approved successfully!') });
      setClasses(prevClasses => prevClasses.map(cls => cls.classid === classId ? { ...cls, status: 'approved' } : cls));
    } catch (error) {
      notification.error({ message: t('Failed to approve class. Please try again.') });
    }
  };

  const TableSummary = ({ classes }) => {
    const totalMinutes = calculateTotalHours(classes);
    const formattedTotal = formatTotalHours(totalMinutes);
    
    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' });
    const periodDisplay = `${monthName} ${selectedYear}`;
    
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={5} className="text-right font-medium">
            {t('Total Hours for')} {periodDisplay}:
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} colSpan={4}>
            <span className="font-bold text-blue-800">{formattedTotal}</span>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

 const columns = [
 {
  title: t("branch"),
  key: "branch_name",
  dataIndex: "branch_name",
  width: 120,
  render: (_, record) => {
    const color = branchColors[record.branch_id] || "default";
    return (
      <Tag color={color}>
        {record.branch_name || t("N/A")}
      </Tag>
    );
  },
},
  { 
    title: t('subject group'), // Add if you want to show subject group
    dataIndex: 'subject_group_name', 
    key: 'subject_group_name', 
    width: 140,
    render: (subject_group_name) => (
      <Tag color="blue" size="small">
        {subject_group_name}
      </Tag>
    ),
  },
  { title: t('classid'), dataIndex: 'classid', key: 'classid', width: 70 },
  { title: t('classcode'), dataIndex: 'class_code', key: 'class_code', width: 120 },
  { title: t('subject'), dataIndex: 'subject_name', key: 'subject_name', width: 140 },
  { 
    title: t('date'), 
    dataIndex: 'date', 
    key: 'date', 
    width: 120, 
    render: formatDate 
  },
  {
    title: t('time'),
    key: 'time_range',
    width: 120,
    render: (_, record) => calculateTimeRange(record.schedule_time, record.schedule_hour),
  },
  { 
    title: t('hours'), 
    dataIndex: 'schedule_hour', 
    key: 'schedule_hour', 
    width: 80,
    render: formatScheduleHour 
  },
  {
    title: t('type'),
    key: 'type',
    width: 100,
    render: (_, record) => (
      <Tag color={record.isprivate ? 'blue' : 'green'}>
        {record.isprivate ? t('private') : t('group')}
      </Tag>
    ),
  },
  {
      title: t('platform'),
      key: 'platform',
      dataIndex: 'platform',
      width: 120,
      render: (platform) => getPlatformDisplay(platform, t),
    },
{
  title: t('approve'),
  key: 'approve',
  width: 180,
  render: (_, record) => {
    // ✅ If already approved, show tag + Unapprove button
    if (record.status === 'approved') {
      return (
        <Space>
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t('HOURSAPPROVED')}
          </Tag>
          <Button
            size="small"
            danger
            shape="circle"
            onClick={() => {
              Modal.confirm({
                title: t('Unapprove Class'),
                content: t('Are you sure you want to unapprove this class?'),
                okText: t('Yes'),
                okType: 'danger',
                cancelText: t('No'),
                onOk: () => handleUnapproveClass(record.classid),
              });
            }}
          >
            X
          </Button>
        </Space>
      );
    }

    // ✅ Not yet approved → show Approve button
    const buttonState = getApprovalButtonState(record.date, record.schedule_time, record.schedule_hour);

    if (!buttonState.visible) return null;

    const button = (
      <Button
        type="primary"
        size="small"
        style={{
          backgroundColor: buttonState.isLate ? '#d9d9d9' : undefined,
          borderColor: buttonState.isLate ? '#d9d9d9' : undefined,
          color: buttonState.isLate ? '#999' : undefined,
          opacity: buttonState.isLate ? 0.6 : 1,
        }}
        onClick={() => handleApprove(record.classid, record.date, record.schedule_time, record.schedule_hour)}
      >
        {buttonState.isLate ? t('Late Approval') : t('confirmteach')}
      </Button>
    );

    return buttonState.tooltip ? <Tooltip title={buttonState.tooltip}>{button}</Tooltip> : button;
  },
}, 
  {
    title: t('payment'),
    key: 'payment',
    width: 100,
    render: (_, record) => (
      <Tag color={record.payout === 'pending' ? 'orange' : 'green'}>
        {record.payout === 'pending' ? t('Pending') : t('Paid')}
      </Tag>
    ),
  },
  {
    title: t('classchange'),
    key: 'classchange',
    width: 120,
    render: (_, record) => {
      const changeLog = classChangeLogs.find(log => log.classid === record.classid);
      const changeReason = changeLog ? changeLog.changereason : '';
      const { text, color } = getChangeReasonDisplay(changeReason);
      return <Tag color={color}>{text}</Tag>;
    },
  },
  {
    title: t('note'),
    key: 'note',
    width: 160,
    render: (_, record) => {
      const changeLog = classChangeLogs.find(log => log.classid === record.classid);
      return changeLog?.note ? (
        <span className="text-gray-600">{changeLog.note}</span>
      ) : (
        <span className="text-gray-400">-</span>
      );
    }
  }
];


  return (
    <Layout >
      <Sidebar />
      <Layout >
        <Header />
        <Content style={{ 
          padding: '24px', 
          marginTop: '60px',
          marginLeft: isMobile ? '0' : '70px'
        }}>
          <Card >
            <Tabs defaultActiveKey="classes">
              <TabPane tab={t('Classes')} key="classes">
                <Space style={{ padding: '24px' }}>
                  <Select
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    style={{ width: 120 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <Option key={month} value={month}>
                        {new Date(0, month - 1).toLocaleString('default', { month: 'long' })}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    value={selectedYear}
                    onChange={handleYearChange}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 10 }, (_, i) => 2024 + i).map(year => (
                      <Option key={year} value={year}>
                        {year}
                      </Option>
                    ))}
                  </Select>

                  <Select
                    value={payoutFilter}
                    onChange={value => setPayoutFilter(value)}
                    style={{ width: 150 }}
                  >
                    <Option value="">{t('All Payouts')}</Option>
                    <Option value="pending">{t('Pending')}</Option>
                    <Option value="paid">{t('Paid')}</Option>
                  </Select>
                </Space>
                
                <div style={{ overflowX: 'auto', maxWidth: '100%', padding: '24px' }}>
                  <Table
                    columns={columns}
                    dataSource={filteredClasses}
                    rowKey="classid"
                    pagination={false} 
                    scroll={{ x: 'max-content' }}
                    style={{ width: '100%' }}
                    summary={() => <TableSummary classes={filteredClasses} />}
                  />
                </div>
              </TabPane>
              
              <TabPane tab={t('Hours Summary')} key="summary">
                <MonthlyHoursSummary teacherId={userId} />
              </TabPane>
              <TabPane tab={t('Payout')} key="payout">
                <TeacherPayout teacherId={userId} />
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(TeacherHours, ['teacher']);